import BrandByCategory, { BrandsByCategory } from 'components/BrandByCategory';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import { ContainerState } from 'containers/MainLayout/types';
import React, { FC, useEffect, useState } from 'react';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { Category, ProductBrand } from 'types/schema';
import { generateSlugFromID } from 'utils/urls/categoriesPathSegment';
import KamereoLogo from '../MainNav/Kamereo.svg';
import Icons from './iconsNewDesign';
import MobileIcons from './iconsNewDesign/mobileIcons';
import TopChoicePanel from './TopChoicePanel';

/* ─── "Top choice" / "Thương hiệu" left-nav sentinels (no real category id) ─── */
const TOP_CHOICE_ID = -1;
const SHOP_BY_BRAND_ID = -2;

/* ─── Backdrop ─────────────────────────────────────────────────────────────── */
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 20;
  background: rgba(0, 0, 0, 0.5);
`;

/* ─── Outer wrapper (sits below SubBar via position absolute on parent) ─────── */
const MenuContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 200;
  display: flex;
  max-width: 1139px;
  height: 100vh;
  max-height: 100vh;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  overflow: hidden;
`;

/* ─── Left panel ────────────────────────────────────────────────────────────── */
const LeftPanel = styled.div`
  width: 295px;
  flex-shrink: 0;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #f0f0f0;
  overflow: hidden;
`;

const LeftHeader = styled.div`
  height: 123px;
  background: #f7f7f8;
  display: flex;
  align-items: center;
  padding: 0 24px;
  flex-shrink: 0;
`;

const CategoryList = styled.div`
  padding: 16px 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
  flex: 1;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox, Safari 18.2+, Chromium 121+ */
  &::-webkit-scrollbar {
    display: none; /* Older Safari and Chromium */
  }
`;

const CategoryItem = styled.div<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  background: ${({ active }) => (active ? '#F5FBF5' : '#ffffff')};
  transition: background 0.15s ease;
  justify-content: space-between;
  &:hover {
    background: #f5fbf5;
  }
`;

const CategoryIconCircle = styled.div<{ active?: boolean }>`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  background: ${({ active }) => (active ? '#E6F9E7' : '#F2F3F5')};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
  overflow: hidden;
`;

const CategoryName = styled.span<{ active?: boolean }>`
  font-size: 14px;
  font-weight: ${({ active }) => (active ? 600 : 500)};
  color: ${({ active }) => (active ? '#3C9041' : '#111827')};
  transition: color 0.15s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChevronRight = styled.div<{ active?: boolean }>`
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-right: 1px solid ${({ active }) => (active ? '#3C9041' : '#4B5563')};
  border-top: 1px solid ${({ active }) => (active ? '#3C9041' : '#4B5563')};
  transform: rotate(45deg);
  margin-right: 4px;
  transition: border-color 0.15s ease;
`;

/* ─── Right panel ───────────────────────────────────────────────────────────── */
const RightPanel = styled.div`
  flex: 1;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

const RightHeader = styled.div<{ height?: boolean }>`
  height: ${({ height }) => (height ? `${height}px` : '124px')};
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 24px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
`;

const CloseButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #374151;
  font-size: 16px;
  line-height: 1;
  background: transparent;
  &:hover {
    background: #f3f4f6;
  }
`;

const RightContent = styled.div`
  display: flex;
  flex: 1;
  background: #e5e7eb;
  gap: 1px;
  overflow: hidden;
`;

const SubcategoryCol = styled.div`
  flex: 1;
  background: #ffffff;
  padding: 20px 40px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  align-content: start;
  overflow-y: auto;
  width: 320px;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox, Safari 18.2+, Chromium 121+ */
  &::-webkit-scrollbar {
    display: none; /* Older Safari and Chromium */
  }
`;

const SubcategoryCard = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  text-decoration: none;
  &:hover .sub-img {
    opacity: 0.85;
  }
`;

const SubcategoryImgWrapper = styled.div<{ colorIndex: number; hasImage?: boolean }>`
  width: 100px;
  height: 100px;
  aspect-ratio: 1;
  border-radius: 50%;
  background: ${({ colorIndex, hasImage }) => {
    if (hasImage) return 'transparent';
    const colors = ['#EBFFEB', '#E9F5FF', '#FFF3E6', '#F0E9FF', '#FFF9E6', '#E6FFF9', '#FFE6E6', '#E6F0FF'];
    return colors[colorIndex % colors.length];
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const SubcategoryName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  text-align: center;
`;

const BrandsCol = styled.div`
  width: 320px;
  flex-shrink: 0;
  background: #ffffff;
  overflow-y: auto;
`;

const TrustedBrandsCol = styled.div`
  width: 460px;
  flex-shrink: 0;
  background: #ffffff;
  overflow-y: auto;
`;

const Icon = styled(SVGIcon)`
  width: 24px;
  height: 24px;
  margin: auto;
  ${({ img }) =>
    img &&
    `
    & > div > img {
      width: 24px;
      height: 24px;
    }
  `}
`;

const ChefIcon = styled.div`
  background: #fff6d4;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* ─── Props ─────────────────────────────────────────────────────────────────── */
interface OwnProps {
  onClose: () => void;
}

interface Props extends OwnProps, RouteComponentProps {
  main?: ContainerState;
}

/* ─── Component ─────────────────────────────────────────────────────────────── */
const CategoryMegaMenu: FC<Props> = ({ main, onClose, location }) => {
  const categories: Category[] = main?.categories || [];
  const brandsByCategory: BrandsByCategory[] = (main?.brandsByCategory as BrandsByCategory[]) || [];
  const trustedBrands = (main?.trustedBrands as ProductBrand[]) || [];
  const bestChoiceBrands = trustedBrands.slice(0, 7);
  const lang = localStorage.getItem('lang') || 'vi';
  const region = main?.buyer?.region ? main.buyer.region : localStorage.getItem('region');

  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);

  const handleOnclose = () => {
    setActiveCategoryId(null);
    onClose();
  };
  // Close on route change
  useEffect(() => {
    handleOnclose();
  }, [location.pathname]);

  const activeCategory = categories.find((c) => c.id === activeCategoryId);
  const targetBrands = brandsByCategory.find((b) => Number(b.categoryId) === activeCategoryId);

  return (
    <>
      <Backdrop onClick={handleOnclose} />
      <MenuContainer onClick={(e) => e.stopPropagation()}>
        {/* ── Left panel ── */}
        <LeftPanel>
          <LeftHeader>
            <SVGIcon src={KamereoLogo} imgAttr={{ height: '34px', width: 'auto' }} />
          </LeftHeader>

          <CategoryList>
            <CategoryItem
              key={TOP_CHOICE_ID}
              active={activeCategoryId === TOP_CHOICE_ID}
              onMouseEnter={() => setActiveCategoryId(TOP_CHOICE_ID)}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flex: 1,
                  minWidth: 0,
                  textDecoration: 'none',
                }}
              >
                <ChefIcon>
                  <Icon src={Icons.chefChoice} />
                </ChefIcon>

                <CategoryName active={activeCategoryId === TOP_CHOICE_ID}>
                  {lang === 'vi' ? 'Lựa chọn hàng đầu' : 'Top choice'}
                </CategoryName>
              </div>
              <ChevronRight active={activeCategoryId === TOP_CHOICE_ID} />
            </CategoryItem>

            {categories.map((cat) => {
              const isActive = cat.id === activeCategoryId;
              return (
                <CategoryItem key={cat.id} active={isActive} onMouseEnter={() => setActiveCategoryId(cat.id ?? null)}>
                  <Link
                    to={`/${generateSlugFromID(cat, lang)}`}
                    onClick={handleOnclose}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      flex: 1,
                      minWidth: 0,
                      textDecoration: 'none',
                    }}
                  >
                    <CategoryIconCircle active={isActive}>
                      {Icons[cat.id ?? 0] && (
                        <SVGIcon src={Icons[cat.id ?? 0]} imgAttr={{ width: '32px', height: '32px' }} />
                      )}
                    </CategoryIconCircle>
                    <CategoryName active={isActive}>{cat.nameLocal}</CategoryName>
                  </Link>
                  <ChevronRight active={isActive} />
                </CategoryItem>
              );
            })}
            <CategoryItem
              key={SHOP_BY_BRAND_ID}
              active={activeCategoryId === SHOP_BY_BRAND_ID}
              onMouseEnter={() => setActiveCategoryId(SHOP_BY_BRAND_ID)}
            >
              <Link
                to={`/thuong-hieu`}
                onClick={handleOnclose}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flex: 1,
                  minWidth: 0,
                  textDecoration: 'none',
                }}
              >
                <CategoryIconCircle active={activeCategoryId === SHOP_BY_BRAND_ID}>
                  {Icons['ShopByBrand'] && (
                    <SVGIcon src={Icons['ShopByBrand']} imgAttr={{ width: '32px', height: '32px' }} />
                  )}
                </CategoryIconCircle>
                <CategoryName active={activeCategoryId === SHOP_BY_BRAND_ID}>
                  {lang === 'vi' ? 'Thương hiệu' : 'Trusted Brands'}
                </CategoryName>
              </Link>
              <ChevronRight active={activeCategoryId === SHOP_BY_BRAND_ID} />
            </CategoryItem>
          </CategoryList>
        </LeftPanel>

        {/* ── Right panel: "Top choice" ── */}
        {activeCategoryId === TOP_CHOICE_ID && (
          <RightPanel>
            <RightHeader height={64}>
              <CloseButton onClick={handleOnclose} aria-label="Close">
                ✕
              </CloseButton>
            </RightHeader>

            <RightContent>
              <TopChoicePanel lang={lang} brands={bestChoiceBrands} region={region} onItemClick={handleOnclose} />
            </RightContent>
          </RightPanel>
        )}

        {/* ── Right panel: category ── */}
        {activeCategory?.id && (
          <RightPanel>
            <RightHeader>
              <CloseButton onClick={handleOnclose} aria-label="Close">
                ✕
              </CloseButton>
            </RightHeader>

            <RightContent>
              {/* Subcategories */}
              <SubcategoryCol>
                {activeCategory?.children?.map((sub, idx) => (
                  <SubcategoryCard key={sub.id} to={`/${generateSlugFromID(sub, lang)}`} onClick={handleOnclose}>
                    <SubcategoryImgWrapper colorIndex={idx} className="sub-img" hasImage={!!MobileIcons[sub.id ?? 0]}>
                      {MobileIcons[sub.id ?? 0] ? (
                        <img
                          src={MobileIcons[sub.id ?? 0]}
                          alt={sub.nameLocal ?? ''}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      ) : Icons[sub.id ?? 0] ? (
                        <SVGIcon src={Icons[sub.id ?? 0]} imgAttr={{ width: '64px', height: '64px' }} />
                      ) : null}
                    </SubcategoryImgWrapper>
                    <SubcategoryName>{sub.nameLocal}</SubcategoryName>
                  </SubcategoryCard>
                ))}
              </SubcategoryCol>

              {/* Brands */}
              <BrandsCol>
                <BrandByCategory brands={targetBrands} isMega={true} />
              </BrandsCol>
            </RightContent>
          </RightPanel>
        )}

        {/* ── Right panel: "Trusted Brands" ── */}
        {activeCategoryId === SHOP_BY_BRAND_ID && (
          <RightPanel>
            <RightHeader>
              <CloseButton onClick={handleOnclose} aria-label="Close">
                ✕
              </CloseButton>
            </RightHeader>

            <RightContent>
              <TrustedBrandsCol>
                <BrandByCategory brands={{ data: trustedBrands }} isMega={true} />
              </TrustedBrandsCol>
            </RightContent>
          </RightPanel>
        )}
      </MenuContainer>
    </>
  );
};

export default withRouter(CategoryMegaMenu);
